import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { X } from 'lucide-react'
import { useAccess } from '@/shared/rbac'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useToast } from '@/shared/components/ui/toast'
import { settingsApi } from '@/features/settings/api/settings.api'
import { useAnnouncementModalStore } from '@/features/announcements/store'
import type { WelcomeAnswer } from '../types'
import { OVERVIEW_TOUR_ID, getTour } from '../lib/tours'
import { getTourStatus, markTourDismissed } from '../utils/progress-storage'
import { isImpersonating } from '../utils/impersonation'
import { ENGINE_COPY } from '../utils/engine-copy'
import { loadSession, saveSession } from '../utils/page-invites'
import { isVisible } from '../utils/wait-for-target'
import { copyToPlain } from '../utils/rich-copy'
import { RichCopy } from './rich-copy'
import { onTourEnd, useWalkthroughStore } from '../stores/walkthrough.store'
import {
  WELCOME_SETTLE_MS,
  WELCOME_VERSION,
  appDialogOpen,
  isInterruptibleRoute,
  countWelcomeView,
  loadLocalOnboarding,
  saveLocalOnboarding,
  seenThisSession,
  shouldShowWelcome,
  welcomeCapReached,
  welcomeCopy,
} from '../utils/onboarding'

const OVERVIEW = getTour(OVERVIEW_TOUR_ID)!

/** The header ? button is on screen (it overflows off a phone-width header). */
const helpReachable = () => {
  const el = document.querySelector('[data-tour="header.help"]')
  return !!el && isVisible(el)
}

/** Re-check cadence while something (a dialog, a running tour) blocks the offer. */
const RECHECK_MS = 1000

/**
 * Persist the answer: local fallback first (so this browser never re-asks even
 * if the PATCH fails), then the server, then mirror it into the auth store so
 * the card can't re-render before the next /auth/me.
 */
async function persistAnswer(userId: string, answer: WelcomeAnswer): Promise<void> {
  saveLocalOnboarding(userId, { ...loadLocalOnboarding(userId), answered: answer })
  // Any welcome answer starts the page-card cooldown: no page card right after it.
  saveSession(userId, { ...loadSession(userId), lastCardAt: Date.now() })
  // Declined without starting → the overview's own per-browser status says so too.
  if (answer === 'dismissed' && getTourStatus(userId, OVERVIEW) === 'not_started') markTourDismissed(userId, OVERVIEW)
  try {
    await settingsApi.updateProfile({
      preferences: { onboarding: { welcome: answer, at: new Date().toISOString(), v: WELCOME_VERSION } },
    })
  } catch {
    /* offline / 5xx / blocked (impersonation): the local fallback stands */
  }
  const current = useAuthStore.getState().user
  if (current?.id === userId) {
    useAuthStore.setState({ user: { ...current, onboarding: { ...current.onboarding, welcome_eligible: false, welcome: answer } } })
  }
}

/**
 * One-time, non-modal welcome card that OFFERS the overview tour — it never
 * starts it on its own. Shown to every user once (server flag), never while
 * impersonating, never over chart review / dispatch / create flows, a 403
 * panel, an open dialog or announcement, or a running tour.
 */
export function WelcomeCard() {
  const user = useAuthStore((s) => s.user)
  const access = useAccess()
  const { pathname } = useLocation()
  const tourActive = useWalkthroughStore((s) => s.activeTourId !== null)
  const startTour = useWalkthroughStore((s) => s.startTour)
  const announcementOpen = useAnnouncementModalStore((s) => s.openId !== null)
  const { addToast } = useToast()

  /** The path the card is currently offered on (null = hidden). */
  const [shownPath, setShownPath] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)

  const userId = user?.id ?? null
  const eligible = user?.onboarding?.welcome_eligible === true && !isImpersonating(user)

  // Latest blockers for the timer callbacks.
  const blockers = useRef({ tourActive, announcementOpen })
  useEffect(() => {
    blockers.current = { tourActive, announcementOpen }
  })

  // On each route: let it settle, then offer if every rule holds; keep
  // re-checking while a dialog / tour blocks it. Leaving the route hides it.
  useEffect(() => {
    if (!userId || !eligible || !isInterruptibleRoute(pathname)) return
    let timer: ReturnType<typeof setTimeout>
    const check = () => {
      const local = loadLocalOnboarding(userId)
      if (local.answered) return
      const seen = seenThisSession(userId)
      if (welcomeCapReached(local, seen)) {
        // Ignored in three sessions: stop nagging, record it like "Not now".
        void persistAnswer(userId, 'dismissed')
        return
      }
      const show = shouldShowWelcome({
        user: useAuthStore.getState().user,
        pathname,
        local,
        seenThisSession: seen,
        accessDenied: !!document.querySelector('[data-access-denied]'),
        dialogOpen: appDialogOpen() || blockers.current.announcementOpen,
        tourActive: blockers.current.tourActive,
      })
      if (!show) {
        timer = setTimeout(check, RECHECK_MS)
        return
      }
      countWelcomeView(userId)
      setShownPath(pathname)
    }
    timer = setTimeout(check, WELCOME_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [pathname, userId, eligible])

  // Any finished overview run answers the welcome (a "Take the tour" run is
  // upgraded to completed). An overview the user ran from the ? menu
  // and closed counts as "Not now"; one started from the card and closed stays
  // "started" — both never re-prompt.
  useEffect(() => {
    if (!userId) return
    return onTourEnd((e) => {
      if (e.tourId !== OVERVIEW_TOUR_ID) return
      const u = useAuthStore.getState().user
      // An impersonator must never answer the user's welcome (the PATCH is blocked anyway).
      if (u?.id !== userId || isImpersonating(u)) return
      const answered = u.onboarding?.welcome ?? loadLocalOnboarding(userId).answered
      // Only an unanswered welcome, or our own "started", is upgraded — replays never re-send.
      if (e.outcome === 'completed') {
        if (!answered || answered === 'started') void persistAnswer(userId, 'completed')
      }
      else if (e.outcome === 'dismissed' && e.source !== 'welcome' && !answered) void persistAnswer(userId, 'dismissed')
    })
  }, [userId])

  // While shown, a dialog opened on top hides it until the dialog closes.
  const [dialogOpen, setDialogOpen] = useState(false)
  const shown = shownPath !== null
  useEffect(() => {
    if (!shown) return
    const id = setInterval(() => setDialogOpen(appDialogOpen()), RECHECK_MS)
    return () => clearInterval(id)
  }, [shown])

  const visible =
    !!userId && eligible && shownPath === pathname && !tourActive && !announcementOpen && !dialogOpen

  if (!visible) return null
  const copy = welcomeCopy(access, helpReachable())

  const answer = async (value: 'started' | 'dismissed') => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    try {
      if (value === 'started') {
        setShownPath(null)
        startTour(OVERVIEW_TOUR_ID, 'welcome')
      }
      await persistAnswer(userId, value)
      if (value === 'dismissed') {
        addToast({
          variant: 'info',
          title: ENGINE_COPY.toast.laterTitle,
          description: copyToPlain(helpReachable() ? ENGINE_COPY.toast.welcomeLater : ENGINE_COPY.toast.welcomeLaterNarrow),
        })
      }
    } finally {
      busyRef.current = false
      setBusy(false)
      setShownPath(null)
      // The card unmounts with focus inside it; land on the ? button it points to.
      if (value === 'dismissed') document.querySelector<HTMLElement>('[data-tour="header.help"]')?.focus()
    }
  }

  return (
    <section className="mc-welcome" role="region" aria-labelledby="mc-welcome-title" aria-live="polite">
      <button
        type="button"
        className="mc-welcome-close"
        aria-label="Dismiss welcome"
        onClick={() => void answer('dismissed')}
        disabled={busy}
      >
        <X size={14} aria-hidden="true" />
      </button>
      <h2 id="mc-welcome-title" className="mc-welcome-title"><RichCopy text={copy.title} /></h2>
      <p className="mc-welcome-body"><RichCopy text={copy.body} /></p>
      <div className="mc-welcome-actions">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => void answer('started')} disabled={busy}>
          Take the tour
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => void answer('dismissed')} disabled={busy}>
          Not now
        </button>
      </div>
    </section>
  )
}
